// @flow

import React, { type Node } from 'react';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { Label, TextField } from '../../../../../commons/atoms';
import { InputGroup } from '../../../../../commons/molecules';
import { useUniqueId, useMustShow } from '@tectonics/hooks';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError, type UserInputValidationError } from '@tectonics/prs/validation';
import { ErrorLabel, InputHolder } from '../commons';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import type { Country } from '@tectonics/prs/country';

export function InputCustomerEmail(): Node {
  const mustShow = useMustShow('customerEmail');
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const validationError = useUserInputValidationError('customerEmail');
  const formHasBeenSent = useFormHasBeenSent();
  const [customerEmail, handleCustomerEmailChange] = useUserInputAsTextField('customerEmail');

  if (!mustShow)
    return null;

  return (
    <InputHolder data-test-name="customer-email">
      <InputGroup
        label={<Label htmlFor={inputId}>E-mail</Label>}
        input={
          <TextField
            id={inputId}
            type="email"
            value={customerEmail}
            onChange={handleCustomerEmailChange}
            readOnly={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            name="customer-email"
          />
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </InputHolder>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>{ EmptyEmailMessages(country) }</ErrorLabel>;

    default:
      return <ErrorLabel>{ InvalidEmailMessages(country) }</ErrorLabel>;
  }
}

function EmptyEmailMessages(country: Country): string {
  switch (country){
    case 'za':
    case 'ke':
      return 'Please enter your e-mail address';
    default:
      return 'Preencha seu email';
  }
}

function InvalidEmailMessages(country: Country): string {
  switch (country){
    case 'za':
    case 'ke':
      return 'Invalid email';
    default:
      return 'Email inválido';
  }
}
